/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FabMessageService } from '@fabric/components/message';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {DISMISS_MSG} from '../../../common/constants';
import { FileUploadService } from '../../../services/file-upload.service';

@Component({
    selector: 'app-upload-button',
    templateUrl: './file-upload-button.component.html',
    styleUrls: ['./file-upload-button.component.scss'],
    standalone: false
})
export class FileUploadButtonComponent {

  fileName = '';
  uploadProgress: number;
  uploadSub: Subscription;
  data: any;
  showSpinner: boolean;

  @Input() apiUrl: string;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter<any>();

  constructor(private readonly uploadService: FileUploadService,
              private readonly snackbar: FabMessageService) {
  }

  /**
   * Triggered when a user selects a file during the upload. It will send the file to the upload service.
   * Emits the response returned from the upload service.
   *
   * @param event The event containing the file
   */
  onFileSelected(event): void {
    // TODO: Add a check for file size
    const file: File = event.target.files[0];
    // If the file exists, try to upload it
    if (file) {
      this.fileName = file.name;
      this.showSpinner = true;
      const upload = this.uploadService.upload(file, this.apiUrl)
      .pipe(
        // Used to reset the progress bar after upload
        finalize(() => this.reset())
      );
      // Subscribe to the upload observable and set the results & progress bar
      this.uploadSub = upload.subscribe((e) => {
          if (e.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round(100 * (e.loaded / e.total));
          }
          if (e.type === HttpEventType.Response) {
            const map = new Map<string, any>();
            map.set('data', e.body);
            map.set('file', file);
            this.data = map;
          }
        },
        err => {
          this.snackbar.displayError(err.error.message, DISMISS_MSG);
          this.showSpinner = false;
        },
        () => {
          // Emit the response data on completion
          this.eventEmitter.emit(this.data);
          this.showSpinner = false;
        });
    }
  }

  /**
   * Cancel the upload
   */
  cancelUpload(): void {
    this.uploadSub.unsubscribe();
    this.reset();
  }

  /**
   * Reset the progress bar
   */
  reset(): void {
    this.uploadProgress = null;
    this.uploadSub = null;
    this.showSpinner = false;
  }
}
